import { Array, identity, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import type {
  NodePropertyName,
  ViewConfigDimension,
  ViewConfigField,
  ViewConfigFilterNode,
  ViewConfigSorter,
  ViewConfigTreeNode,
  ViewConfigUiComponent,
} from '../../../../schemas/view_config/index.ts'
import { flatNodes } from '../../../query/executeViewQuery/util.ts'

export function pruneSheetConfigTree({
  tree,
  fields,
  filterTree,
  sorters,
  components,
  nodeNames,
  dimensionNodeSearcher,
}: {
  tree?: ViewConfigTreeNode
  fields?: ViewConfigField[]
  filterTree?: ViewConfigFilterNode
  sorters?: ViewConfigSorter[]
  components?: ViewConfigUiComponent[]
  nodeNames?: string[]
  dimensionNodeSearcher?: (dimension: ViewConfigDimension) => NodePropertyName['nodeName'] | undefined
}): ViewConfigTreeNode | undefined {
  if (tree === undefined) {
    return undefined
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: overrideしているとき、特にadditionalFieldsを指定しているときに、動かなくなる可能性
  const usingNodeNames = pipe(
    [
      ...(fields ?? []).map((x) => x.property.nodeName),
      ...(sorters ?? []).map((x) => x.property.nodeName),
      ...flatNodes(filterTree)
        .flatMap((x) => x.leafs)
        // eslint-disable-next-line array-callback-return
        .map((x) => {
          const { type } = x
          switch (type) {
            case 'property': {
              return x.property.nodeName
            }
            case 'dimension': {
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
              if (x.dimension === undefined || dimensionNodeSearcher === undefined) {
                // eslint-disable-next-line array-callback-return
                return
              }
              return dimensionNodeSearcher(x.dimension)
            }
            // あまりないはず
            default:
          }
        }),
      ...(components ?? []).flatMap((x) => flatNodes(x)).map((x) => x.property?.nodeName),
      ...(nodeNames ?? []),
    ],
    Array.dedupe,
    Array.filter(isNotNullish),
  )

  return rec(tree, usingNodeNames) ?? tree // rootが削除されることはなく、recがundefinedを返すこともないはずだが、undefinedになった際には一応元のtreeを返す
}

function rec(node: ViewConfigTreeNode, validNodeNames: string[]): ViewConfigTreeNode | undefined {
  // 直接ノードを使用していなくても、childrenを使用していれば残す必要がある
  const children = Array.flatMapNullishOr(
    (node.children ?? []).map((child) => rec(child, validNodeNames)),
    identity,
  )
  if (!validNodeNames.includes(node.name) && children.length === 0) {
    return undefined
  }

  return {
    ...node,
    children,
  }
}
